import type { Adjustment, MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { ATTACHMENTS, getRackModel } from "../../../modelPaths"
import { CaveOptions, CTTowerOptions, LatAndLowRowAttachmentSelection, RackOptions, TrolleyAttachmentSelection } from "../../../RackTypes"
import { RACK_COLORS } from "../../../constants"
import { CTM1Builder } from "../../ctm1/ctm1Builder"
import { IndyBuilder } from "../../rack/functionalTrainer/FM/indy/indyBuilder"
import { SlingerXBuilder } from "../../rack/functionalTrainer/FM/slinger-x/slingerXBuilder"

export class CaveCableSystemBuilder {
  protected config: CaveOptions
  protected material: MaterialInfo

  constructor(config: CaveOptions, material: MaterialInfo) {
    this.config = config
    this.material = material
  }

  // MARK: Attachments
  getLatBarAttachment(left: boolean): string | undefined {
    return left
      ? this.config.leftLatBarAttachment
      : this.config.rightLatBarAttachment
  }

  getCTM1SeatSelection(left: boolean): string | undefined {
    return left
      ? this.config.leftSeatSelection
      : this.config.rightSeatSelection
  }
    
  createLatBarAttachment(left: boolean): PartDefinition {
    const attachment = this.getLatBarAttachment(left) ?? ''
    const key = left ? 'left' : 'right'
      
    return {
      mesh      : `${ATTACHMENTS}#${attachment}`,
      slots     : {},
      materials : this.config.materials?.[key]?.latpulldown as MaterialInfo,
    }
  }
  
  createLatClip(left: boolean): PartDefinition {
    const attachment = this.getLatBarAttachment(left)
  
    return {
      mesh  : `${ATTACHMENTS}#clip`,
      slots : {
        bottom: attachment ? [this.createLatBarAttachment(left)] : [],
      },
    }
  }

  createTrolleyClip(left: boolean): PartDefinition {
    const attachment = this.createTrolleyAttachment(left)
  
    return {
      mesh  : `${ATTACHMENTS}#clip`,
      slots : {
        bottom: this.getTrolleyAttachment(left) ? [attachment] : [],
      },
    }
  }
  
  getLowRowAttachment(left: boolean): string | undefined {
    return left
      ? this.config.leftLowRowAttachment
      : this.config.rightLowRowAttachment
  }
  
  createLowRowAttachment(left: boolean): PartDefinition {
    const attachment = this.getLowRowAttachment(left) ?? ""
  
    return {
      mesh  : `${ATTACHMENTS}#${attachment}`,
      slots : {},
    }
  }

  getTrolleyAttachment(left: boolean): string | undefined {
    return left
      ? this.config.leftTrolleyAttachment
      : this.config.rightTrolleyAttachment
  }

  createTrolleyAttachment(left: boolean): PartDefinition {
    const attachment = this.getTrolleyAttachment(left) ?? ""
  
    return {
      mesh  : `${ATTACHMENTS}#${attachment}`,
      slots : {},
    }
  }
  
  createLowRowClip(left: boolean): PartDefinition {
    const attachment = this.getLowRowAttachment(left)
  
    return {
      mesh  : `${ATTACHMENTS}#clip`,
      slots : {
        bottom: attachment ? [this.createLowRowAttachment(left)] : [],
      },
    }
  }
  
  // MARK: CT1
  createCT1(left: boolean): PartDefinition {
    return {
      mesh  : getRackModel('CAVE_CT1', 'ct-1'),
      slots : {
        top           : this.createTrolleyClip(left),
        left_sticker  : this.createLeftCT1Sticker(left),
        right_sticker : this.createRightCT1Sticker(left),
      },
      anchor      : 'bottom',
      adjustments : [
        { type: 'rotate', axis: 'Y', amount: 180 },
        { type: 'flip', axis: 'X' },
      ],
    }
  }

  createLeftCT1Sticker(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('CAVE_CT1', 'rogue sticker'),
      slots       : {},
      adjustments : left ? [] : [
        { type: 'rotate', axis: 'Y', amount: 180 },
        { type: 'flip', axis: 'X' },
      ],
    }
  }

  createRightCT1Sticker(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('CAVE_CT1', 'rogue sticker'),
      slots       : {},
      adjustments : left ? [
        { type: 'rotate', axis: 'Y', amount: 180 },
        { type: 'flip', axis: 'X' },
      ] : [],
    }
  }
  
  createCT1Shroud(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('CAVE_CT1', 'ct-sheath'),
      slots       : {},
      anchor      : 'top',
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
    }
  }
  
  // MARK: LP2
  createLP2(left: boolean): PartDefinition {
    return {
      mesh  : getRackModel('CAVE_LP2', 'lp-2'),
      slots : {
        top    : this.createLatClip(left),
        bottom : this.createLowRowClip(left),
      },
      anchor      : 'bottom',
      adjustments : left ? [{ type: 'flip', axis: 'Z'}] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset      : { y: -2 },
    }
  }
  
  // MARK: CTM1
  createCTM1(left: boolean) {
    const lat = this.getLatBarAttachment(left)
    const low = this.getLowRowAttachment(left)
    const trolley = this.getTrolleyAttachment(left)
    const seatSelection = this.getCTM1SeatSelection(left)
  
    const leftCtm1Materials =  {...this.config.materials, latpulldown: this.config.materials?.left?.latpulldown as CTTowerOptions['materials']}
    const rightCtm1Materials =  {...this.config.materials, latpulldown: this.config.materials?.right?.latpulldown as CTTowerOptions['materials']} 
    
    const ctm1Config: CTTowerOptions = {
      line        : 'RM',
      numbered    : true,
      height      : this.config.height as string,
      color       : RACK_COLORS['Satin Black'],
      hardware    : 'isonite',
      weightStack : this.config[left ? 'leftCableSystem' : 'rightCableSystem']?.includes('250')
        ? '250'
        : '300',
      trolleySelection : trolley as TrolleyAttachmentSelection | undefined,
      latBarSelection  : lat as LatAndLowRowAttachmentSelection | undefined,
      lowRowSelection  : low as LatAndLowRowAttachmentSelection | undefined,
      seatSelection,
      bandPegs         : false,
      standAlone       : true,
      materials        : left ? leftCtm1Materials as CTTowerOptions['materials'] : rightCtm1Materials as CTTowerOptions['materials'],
    }
    
    const ctm1 = new CTM1Builder(ctm1Config, left).build()
    ctm1.anchor = 'back'
    ctm1.slots.back_base = [] // Override the back base 
    ctm1.slots.back = [] // Override the back upright 
    ctm1.offset = { y: 1 }
    ctm1.adjustments = [{ type: 'rotate', axis: 'Y', amount: 180 }]

    // Trolley handles need to face the right on both sides
    if (left) {

      ctm1.adjustments = [
        { type: 'rotate', axis: 'Y', amount: 180 },
        { type: 'flip', axis: 'X' },
      ] 

      const ctm1Shroud90 = ctm1.slots.shroud_90h as PartDefinition
      const ctm1Shroud100 = ctm1.slots.shroud_100h as PartDefinition

      // Ensure left side shroud is facing the correct way
      if (ctm1Shroud90) {
        ctm1Shroud90.adjustments = []
      } else if (ctm1Shroud100) {
        ctm1Shroud100.adjustments = []
      }

    }
        
    return ctm1
  }
  
  // MARK: Slinger (legacy static mesh — remove once CAVE_OLD_SLINGER_SKU is retired from the catalog)
  createSlingerShroud(left: boolean): PartDefinition {
    return {
      mesh   : getRackModel('CAVE_RM_SLINGER', 'shroud'),
      slots  : {},
      anchor : 'top',
      offset : left ? { y: -8, z: -0.35 } : { y: -8, z: 26.7 },
    }
  }

  createSlinger(left: boolean): PartDefinition {
    const latClip = this.createLatClip(left)
    if (!left) latClip.adjustments = [{ type: 'flip', axis: 'X' }]

    return {
      mesh  : getRackModel('CAVE_RM_SLINGER', `slinger ${this.config.height}`),
      slots : {
        bottom: latClip,
      },
      anchor      : 'back',
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
      offset      : { z: -4, y: 0.2 },
    }
  }

  createSlingerLowPulley(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('CAVE_RM_SLINGER', 'low pulley'),
      slots       : {},
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset      : { x: 1, y: -8 },
    }
  }

  createSlingerSeat(left: boolean): PartDefinition {
    const attachment = this.getLowRowAttachment(left)

    return {
      mesh  : getRackModel('CAVE_RM_SLINGER', 'lat pulldown seat'),
      slots : {
        bottom: {
          mesh  : `${ATTACHMENTS}#bottom clip`,
          slots : { front: attachment ? [this.createLowRowAttachment(left)] : [] },
        },
      },
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset      : { x: 1.4, y: -6 },
    }
  }

  // MARK: Slinger X
  // Replaces the legacy single-mesh Slinger.
  buildSlingerXSystem(cableUpright: PartDefinition, topPulleyCrossmember: PartDefinition, bottomPulleyCrossmember: PartDefinition, left: boolean): void {
    const shrouds = left ? this.config.leftShrouds : this.config.rightShrouds
    const weightStackAmount = this.config[left ? 'leftCableSystem' : 'rightCableSystem']?.includes('300') ? '300' : '250'
    const lat = this.getLatBarAttachment(left)
    const low = this.getLowRowAttachment(left)

    const slingerXConfig: RackOptions = {
      line                : 'RM',
      numbered            : true,
      height              : this.config.height as string,
      halfRack            : true,
      depth               : '2', // 24" only
      color               : RACK_COLORS['Satin Black'],
      hardware            : 'isonite',
      sixPost             : false,
      slingerX            : true,
      isFunctionalTrainer : true,
      ftShrouds           : shrouds,
      weightStackAmount,
      swivelPulley        : true, // Cave Slinger X always includes the bottom swivel pulley — no option to remove it
      latBarSelection     : lat as LatAndLowRowAttachmentSelection | undefined,
      lowRowSelection     : low as LatAndLowRowAttachmentSelection | undefined,
      materials           : this.config.materials,
      accessories         : [],
    }

    const parts = new SlingerXBuilder(slingerXConfig).createCaveAssembly(left, cableUpright)
    const backPulley: PartDefinition = parts.backPulley
    const swivelPulley: PartDefinition | undefined = parts.swivelPulley

    backPulley.offset = { z: 0.1 }

    // The Cave crossmembers/upright mirror the right with rotate-Y-180 + flip-X.
    if (!left) {
      const rotateY180: Adjustment = { type: 'rotate' as const, axis: 'Y' as const, amount: 180 }
      const rightParts = [backPulley, parts.frontPulley, swivelPulley]

      backPulley.offset = { z: -12.1 }

      // Prepend the Y-180 rotate so the parts sit in the same mirrored frame as their parents.
      rightParts.forEach(part => {
        if (!part) return
        
        part.adjustments = [rotateY180, ...(part.adjustments ?? [])]
      })
    }

    // Mirrors SlingerXBuilder.addSide()'s halfRack/non-RML branch — back pulley on side_2, weight stack on side_3.
    topPulleyCrossmember.slots['side_2'] = backPulley
    bottomPulleyCrossmember.slots['side_3'] = parts.weightStack
    cableUpright.slots.top = parts.frontPulley
  }

  // MARK: INDY
  buildIndySystem(topPulleyCrossmember: PartDefinition, bottomPulleyCrossmember: PartDefinition, left: boolean): void {
    const trolley = this.getTrolleyAttachment(left)
    // Select left/right specific config values
    const shrouds = left ? this.config.leftShrouds : this.config.rightShrouds
    const weightStack = this.config[left ? 'leftCableSystem' : 'rightCableSystem']?.includes('250')
      ? '250'
      : '300'

    const indyDepth = left ? this.config.leftIndyDepth : this.config.rightIndyDepth

    const indyConfig: RackOptions = {
      line              : 'RM',
      numbered          : true,
      height            : this.config.height as string,
      depth             : indyDepth,
      color             : RACK_COLORS['Satin Black'],
      hardware          : 'isonite',
      sixPost           : false,
      ftShrouds         : shrouds,
      weightStackAmount : weightStack,
      trolleySelection  : trolley as TrolleyAttachmentSelection | undefined,
      materials         : this.config.materials,
      accessories       : [],
    }

    const indyBuilder = new IndyBuilder(indyConfig)
    const indyParts = indyBuilder.createCaveAssembly(left)
    const indyPoles = indyParts.sideMeshes[0]
    const indyWeightStack = indyParts.sideMeshes[1]

    // Depth 2: no natural indy offset; apply cave push-back correction to both sides.
    if (indyDepth === '2') {
      indyParts.bottomPulley.offset = { z: 1.3 }
      indyPoles.offset = { z: 1.3 }
      indyWeightStack.offset = { z: 1.3 }
    }

    // Left side: cave depth-specific corrections.
    if (left) {
      if (indyDepth === '3') {
        indyParts.topPulley.offset = { z: 0.5 }
      }
      if (indyDepth === '4' && indyParts.topBackPulley) {
        indyParts.topBackPulley.offset = { z: -30 }
      }
    }

    // Right side: crossmember is anchor 'front' with no rotation — add rotate Y 180 and depth offsets.
    if (!left) {
      indyParts.topPulley.adjustments = [...(indyParts.topPulley.adjustments || []), { type: 'rotate', axis: 'Y', amount: 180 }]
      if (indyDepth === '2') {
        indyParts.topPulley.offset = { z: -14 }
      } else if (indyDepth === '3') {
        indyParts.topPulley.offset = { z: -23.5 }
      } else if (indyDepth === '4') {
        indyParts.topPulley.offset = { z: -35.8 }
        if (indyParts.topBackPulley) {
          indyParts.topBackPulley.adjustments = [...(indyParts.topBackPulley.adjustments || []), { type: 'rotate', axis: 'Y', amount: 180 }]
          indyParts.topBackPulley.offset = { z: -2 }
        }
      }
    }

    topPulleyCrossmember.slots['side_1'] = indyParts.topPulley
    bottomPulleyCrossmember.slots['4'] = [...indyParts.sideMeshes, indyParts.bottomPulley]

    // Depth 4: add back pulleys for the 43" cable redirect at the rear.
    if (indyDepth === '4') {
      if (indyParts.topBackPulley) topPulleyCrossmember.slots['side_2'] = indyParts.topBackPulley
      if (indyParts.bottomBackPulley) bottomPulleyCrossmember.slots['4'] = [...indyParts.sideMeshes, indyParts.bottomPulley, indyParts.bottomBackPulley]
    }
  }
}